/* ------------------------------------------------------------------ *
 *  OmopTable – smart container for any OMOP table                    *
 *  Uses Redux slice  state.ive.omop                                  *
 * ------------------------------------------------------------------ */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Container, AppBar, Toolbar, Typography, Chip, Grid, Stack, Icon,
  CircularProgress, TableContainer, Paper, TablePagination,
  FormControl, InputBase,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { BiSearchAlt } from "react-icons/bi";
import { MdFilterList, MdOutlineViewColumn } from "react-icons/md";
import { CommonTable } from "./Table";
import FilterSidebar   from "./FilterSidebar";
import { UtilSidebar } from "./UtilSidebar";
import { NoData }      from "./Nodata";
import { getOmop, updateOmopFilters, updateOmopPage, updateOmopPageSize }
  from "../redux/omop";

/* ---------- search input styles ---------------------------------- */
const Search = styled(FormControl)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.25) },
  width: "100%",
}));
const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  position: "absolute",
  pointerEvents: "none",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));
const StyledInput = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: "100%",
  },
}));

/* ---------- pagination helpers ----------------------------------- */
const BASE_SIZES = [10, 20, 30, 50];
const legalSize  = (sz) => (BASE_SIZES.includes(sz) ? sz : BASE_SIZES[0]);
const sizeOpts   = (current) =>
  BASE_SIZES.includes(current)
    ? BASE_SIZES
    : [...BASE_SIZES, current].sort((a, b) => a - b);

/* ------------------------------------------------------------------ */
export default function OmopTable({ tableName }) {
  /* slice from Redux */
  const dispatch = useDispatch();
  const slice = useSelector(
    (s) => s.ive?.omop?.tables?.[tableName] ?? {}
  );

  const {
    data      = [],
    count     = "?",
    filters   = {},
    page      = 1,
    pageSize  = 25,
    loading   = false,
    error     = null,
  } = slice;

  /* local UI state */
  const [hiddenCols, setHiddenCols] = useState([]);
  const [openCols,   setOpenCols]   = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [draft,      setDraft]      = useState(filters);

  /* concept-ID dropdowns */
  const conceptOptions = useMemo(() => {
    if (!data.length) return {};
    return Object.keys(data[0])
      .filter((k) => k.endsWith("_concept_id"))
      .reduce((acc, col) => {
        acc[col] = [...new Set(data.map((r) => r[col]))]
          .filter((v) => v != null)
          .sort((a, b) => a - b);
        return acc;
      }, {});
  }, [data]);

  const headers        = data.length ? Object.keys(data[0]) : [];
  const visibleHeaders = headers.filter((h) => !hiddenCols.includes(h));

  /* fetch rows when deps change */
  useEffect(() => {
    dispatch(getOmop(tableName));
  }, [dispatch, tableName, page, pageSize, filters]);

  /* ---------- render --------------------------------------------- */
  return (
    <Container maxWidth="xl">
      {/* title */}
      <AppBar elevation={0} position="static" sx={{ bgcolor:"transparent", color:"ButtonText" }}>
        <Toolbar>
          <Typography variant="h6">
            {tableName}
            <Chip size="small" label={count} sx={{ ml: 1 }} />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* search + icons */}
      <AppBar position="static" color="inherit" elevation={0}
              sx={{ bgcolor:"rgba(0,0,0,0)", mt:2, mb:2, pr:1 }}>
        <Grid container alignItems="center">
          <Grid item xs>
            <Search>
              <SearchIconWrapper><BiSearchAlt/></SearchIconWrapper>
              <StyledInput
                placeholder="Search…"
                value={filters.search || ""}
                onChange={(e)=>{
                  dispatch(updateOmopFilters(tableName,{
                    ...filters,
                    search: e.target.value,
                  }));
                }}
              />
            </Search>
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={1}>
              <Icon><MdFilterList
                style={{cursor:"pointer"}}
                onClick={()=>{ setDraft(filters); setOpenFilter(true); }}
              /></Icon>
              <Icon><MdOutlineViewColumn
                style={{cursor:"pointer"}}
                onClick={()=>setOpenCols(true)}
              /></Icon>
            </Stack>
          </Grid>
        </Grid>
      </AppBar>

      {/* table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Stack alignItems="center" sx={{height:400,pt:6}}>
            <CircularProgress/><Typography sx={{mt:2}}>Loading…</Typography>
          </Stack>
        ) : error ? (
          <Typography sx={{p:2}} color="error">{error}</Typography>
        ) : data.length ? (
          <>
            <div style={{ overflow:"auto" }}>
              <CommonTable
                table={tableName}
                headers={visibleHeaders}
                columns={visibleHeaders}
                data={data}
              />
            </div>
            <TablePagination
              rowsPerPageOptions={sizeOpts(pageSize)}
              component="div"
              count={count==="?"?-1:count}
              rowsPerPage={legalSize(pageSize)}
              page={page-1}
              onPageChange={(_,p)=>dispatch(updateOmopPage(tableName,p+1))}
              onRowsPerPageChange={(e)=>{
                dispatch(updateOmopPageSize(tableName, Number(e.target.value)));
              }}
            />
          </>
        ) : <NoData />}
      </TableContainer>

      {/* column chooser */}
      {data.length>0 && (
        <UtilSidebar
          header="Columns"
          footer="Clear All"
          data={headers}
          openFilter={openCols}
          onCloseFilter={()=>setOpenCols(false)}
          handleCheckedItem={(c)=>{
            setHiddenCols((prev)=>
              prev.includes(c) ? prev.filter((x)=>x!==c) : [...prev,c]
            );
          }}
        />
      )}

      {/* filter drawer */}
      {data.length>0 && (
        <FilterSidebar
          openFilter={openFilter}
          onCloseFilter={()=>setOpenFilter(false)}
          headers={headers}
          sampleRow={data[0]}
          conceptOptions={conceptOptions}
          filters={draft}
          onFilterChange={(k,v)=>setDraft(p=>({...p,[k]:v}))}
          onApply={()=>{
            dispatch(updateOmopFilters(tableName,draft));
            setOpenFilter(false);
          }}
          onClear={()=>{
            dispatch(updateOmopFilters(tableName,{}));
            setDraft({});
            setOpenFilter(false);
          }}
        />
      )}
    </Container>
  );
}
